/**
 * Market Thread View Component
 * 오퍼 관련 DM 스레드 표시 (시스템 DM + 사용자 DM)
 */

import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { useAuth } from "@/contexts/AuthContext";
import { useToast } from "@/hooks/use-toast";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { useMutation, useQuery } from "@tanstack/react-query";
import { formatDistanceToNow } from "date-fns";
import { ko } from "date-fns/locale";
import { Loader2, MessageCircle } from "lucide-react";
import { useState } from "react";

interface SystemMessage {
  id: number;
  is_system: boolean;
  template_key: string | null;
  body: string;
  sender_uid?: string | null;
  payload: {
    actions?: Array<{
      type: "POST" | "DRAWER";
      label: string;
      href?: string;
      open?: string;
      prefill?: Record<string, any>;
    }>;
  } | null;
  created_at: string;
}

interface ThreadViewProps {
  threadId: string;
  offerId: number;
  onClose?: () => void;
}

export function ThreadView({ threadId, offerId, onClose }: ThreadViewProps) {
  const { user } = useAuth();
  const { toast } = useToast();
  const [drawerState, setDrawerState] = useState<{
    name: string;
    prefill?: Record<string, any>;
  } | null>(null);

  // Fetch thread messages
  const { data: threadData, isLoading } = useQuery<{
    thread_id: string;
    messages: SystemMessage[];
  }>({
    queryKey: ["/api/market/messages/thread", threadId],
    queryFn: async () => {
      const res = await fetch(`/api/market/messages/thread/${threadId}`);
      if (!res.ok) throw new Error("Failed to fetch thread");
      return res.json();
    },
    enabled: !!threadId,
    refetchInterval: 5000, // 5초마다 폴링
  });

  // CTA 액션 실행
  const executeActionMutation = useMutation({
    mutationFn: async (action: {
      type: "POST" | "DRAWER";
      href?: string;
      open?: string;
      prefill?: Record<string, any>;
    }) => {
      if (action.type === "POST" && action.href) {
        return apiRequest("POST", action.href, {});
      } else if (action.type === "DRAWER" && action.open) {
        setDrawerState({ name: action.open, prefill: action.prefill });
        return Promise.resolve({});
      }
      throw new Error("Invalid action");
    },
    onSuccess: (data, variables) => {
      if (variables.type === "POST") {
        queryClient.invalidateQueries({
          queryKey: ["/api/market/offers"],
        });
        queryClient.invalidateQueries({
          queryKey: ["/api/market/messages/thread", threadId],
        });
        toast({
          title: "완료",
          description: "요청이 처리되었습니다",
        });
      }
    },
    onError: (error: Error) => {
      toast({
        title: "오류",
        description: error.message || "요청 처리에 실패했습니다",
        variant: "destructive",
      });
    },
  });

  if (isLoading) {
    return (
      <Card>
        <CardContent className="p-6">
          <div className="flex items-center justify-center py-8">
            <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
          </div>
        </CardContent>
      </Card>
    );
  }

  const messages = threadData?.messages || [];

  return (
    <Card className="h-[600px] flex flex-col">
      <CardContent className="flex-1 flex flex-col p-0">
        {/* 헤더 */}
        <div className="p-4 border-b flex items-center justify-between">
          <div className="flex items-center gap-2">
            <MessageCircle className="w-5 h-5 text-muted-foreground" />
            <h3 className="font-semibold">거래 메시지</h3>
          </div>
          {onClose && (
            <Button variant="ghost" size="sm" onClick={onClose}>
              닫기
            </Button>
          )}
        </div>

        {/* 메시지 목록 */}
        <div className="flex-1 overflow-y-auto p-4 space-y-4">
          {messages.length === 0 ? (
            <div className="text-center text-muted-foreground py-8">
              메시지가 없습니다
            </div>
          ) : (
            messages.map((msg) => {
              const isSystem = msg.is_system;
              const timeAgo = formatDistanceToNow(new Date(msg.created_at), {
                addSuffix: true,
                locale: ko,
              });

              return (
                <div key={msg.id} className="space-y-2">
                  {isSystem ? (
                    // 시스템 DM
                    <div className="bg-muted/50 rounded-lg p-4 space-y-3">
                      <div className="flex items-start gap-2">
                        <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                          <MessageCircle className="w-4 h-4 text-primary" />
                        </div>
                        <div className="flex-1 space-y-2">
                          <p className="text-sm font-medium text-muted-foreground">
                            시스템 알림
                          </p>
                          <p className="text-sm whitespace-pre-wrap">
                            {msg.body}
                          </p>
                          {msg.payload?.actions && (
                            <div className="flex flex-wrap gap-2 pt-2">
                              {msg.payload.actions.map((action, idx) => (
                                <Button
                                  key={idx}
                                  size="sm"
                                  variant={
                                    action.type === "POST"
                                      ? "default"
                                      : "outline"
                                  }
                                  onClick={() => {
                                    executeActionMutation.mutate(action);
                                  }}
                                  disabled={executeActionMutation.isPending}
                                >
                                  {action.label}
                                </Button>
                              ))}
                            </div>
                          )}
                        </div>
                      </div>
                      <p className="text-xs text-muted-foreground">{timeAgo}</p>
                    </div>
                  ) : (
                    // 사용자 DM
                    <div
                      className={`flex gap-3 ${
                        msg.sender_uid === user?.uid ? "flex-row-reverse" : ""
                      }`}
                    >
                      <Avatar className="w-8 h-8">
                        <AvatarFallback>
                          {msg.sender_uid === user?.uid ? "나" : "상대"}
                        </AvatarFallback>
                      </Avatar>
                      <div
                        className={`max-w-[75%] rounded-2xl px-3 py-2 ${
                          msg.sender_uid === user?.uid
                            ? "bg-primary/10"
                            : "bg-surface-0 shadow-sm border"
                        }`}
                      >
                        <p className="text-sm whitespace-pre-wrap">
                          {msg.body}
                        </p>
                        <p className="text-xs text-muted-foreground mt-1">
                          {timeAgo}
                        </p>
                      </div>
                    </div>
                  )}
                </div>
              );
            })
          )}
        </div>
      </CardContent>
    </Card>
  );
}
